import ReadThrough from '../components/ReadThrough';
import RuntimeBadge from '../components/Editor/RuntimeBadge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { resolveOsView } from '../lib/osEntry';
import { adminApi } from '../lib/api';
import { shouldWarnBeforeUnload, warnBeforeUnload } from '../lib/beforeUnload';
import type { Script, ScriptStatus, ScriptFeatures } from '../../shared/types';
import { isStatusShareable, DEFAULT_FEATURES } from '../../shared/types';
import { EMPTY_SCRATCH, parseScratch, serializeScratch, type ScratchBlock, type ScratchDoc } from '../../shared/scratch';
import { Editor, type SelectionInfo, type TiptapEditor } from '../components/Editor/Editor';
import { Toolbar } from '../components/Editor/Toolbar';
import { useAutosave } from '../components/Editor/useAutosave';
import type { ThreadAnchor } from '../components/Editor/CommentDecorations';
import DiffView, { type Hunk } from '../components/Editor/DiffView';
import EditorTopBar from '../components/EditorTopBar/EditorTopBar';
import SelectionPill from '../components/SelectionPill';
import CommentSidebar from '../components/CommentSidebar';
import WordCount from '../components/Editor/WordCount';
import ClientNotesPanel from '../components/ClientNotesPanel';
import DashboardSlugPanel from '../components/DashboardSlugPanel';
import ClientEmailPanel from '../components/ClientEmailPanel';
import ApproveByPanel from '../components/ApproveByPanel';
import ReviewChecklistPanel from '../components/ReviewChecklistPanel';
import RecordingsPanel from '../components/RecordingsPanel';
import SuggestionPanel from '../components/SuggestionPanel';
import CompareReviewBar from '../components/CompareReviewBar';
import VersionHistoryPanel from '../components/VersionHistoryPanel';
import { usePublishControls } from '../components/Publish/usePublishControls';
import ConfirmModal from '../components/ConfirmModal';
import Toaster, { showToast } from '../components/Toaster';
import CutdownDialog from '../components/Cutdown/CutdownDialog';
import { createdToast } from '../components/Cutdown/cutdownCopy';
import { useCutdownReason } from '../components/Cutdown/useCutdownReason';
import AdminSettingsPanel, { AdminSettingsIcon } from '../components/AdminSettingsPanel';
import SidebarRail, { SidebarPanels, RailIcon, type RailPanel } from '../components/SidebarRail';
import ScratchpadPane from '../components/Scratchpad/ScratchpadPane';
import FieldsPanel from '../components/Brief/FieldsPanel';
import HiddenFieldsStrip from '../components/Brief/HiddenFieldsStrip';
import { useScratchAutosave } from '../components/Scratchpad/useScratchAutosave';
import { scratchBodyToText } from '../components/Scratchpad/scratchText';
import type { PromoteAction } from '../components/Scratchpad/PromoteMenu';
import {
  insertParagraphsAtCursor,
  insertScriptRowAtCursor,
} from '../components/Editor/insertAtCursor';
import { useScriptComments } from '../lib/useScriptComments';
import { useAdminSuggestion } from '../lib/useAdminSuggestion';
import { downloadBriefPdf, downloadScriptPdf } from '../lib/downloadPdf';
import { parseDocJson } from '../lib/parseDoc';
import { openShootView } from '../lib/openShootView';
import { extractInterviewQuestions } from '../../shared/extractInterviewQuestions';
import { hasShootView } from '../../shared/scriptRows';
import { isBriefTemplate } from '../../shared/briefFields';
import { mergeDocs } from '../../shared/publish';
import { useRefImageSource } from '../lib/refImageSource';

// Scratch pane width as a percentage of the main area. Per browser, not per
// script: it is a working preference, not a property of the document.
const SCRATCH_WIDTH_KEY = 'hsp_scratch_width';
const SCRATCH_WIDTH_DEFAULT = 38;
const SCRATCH_WIDTH_MIN = 20;
const SCRATCH_WIDTH_MAX = 70;
const SCRATCH_COLUMN_MIN_PX = 312;

function readStoredScratchWidth(): number {
  try {
    const raw = window.localStorage.getItem(SCRATCH_WIDTH_KEY);
    const value = raw === null ? NaN : Number(raw);
    if (
      Number.isFinite(value) &&
      value >= SCRATCH_WIDTH_MIN &&
      value <= SCRATCH_WIDTH_MAX
    ) return value;
  } catch {
    // Private browsing / storage disabled — the default is fine.
  }
  return SCRATCH_WIDTH_DEFAULT;
}

function isMobileViewport(): boolean {
  return window.matchMedia('(max-width: 767px)').matches;
}

export default function AdminEditScript() {
  const { id } = useParams<{ id: string }>();
  // Reference images (2B) in this page's editor, diff, preview and PDF.
  useRefImageSource(id ? { kind: 'admin', scriptId: id } : null);
  const navigate = useNavigate();
  const location = useLocation();
  const consumedEntry = useRef<string | null>(null);
  const [viewNotice, setViewNotice] = useState<string | null>(null);
  const [script, setScript] = useState<Script | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editor, setEditor] = useState<TiptapEditor | null>(null);
  const [version, setVersion] = useState(0);
  const [status, setStatus] = useState<ScriptStatus>('draft');
  const [features, setFeatures] = useState<ScriptFeatures>(DEFAULT_FEATURES);
  const [compareMode, setCompareMode] = useState(false);
  const [mobileComparePane, setMobileComparePane] = useState<'master' | 'suggestion'>('suggestion');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cutdownOpen, setCutdownOpen] = useState(false);
  const cutdownReason = useCutdownReason(editor);
  // Interview briefs only: the Fields drawer and the header button that owns it.
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const fieldsButtonRef = useRef<HTMLButtonElement>(null);
  const [resetConfirming, setResetConfirming] = useState(false);
  // Bumped when something replaces the master doc out-of-band (suggestion
  // accept, version restore). Keying the editor by this triggers a remount
  // so the new doc loads. We DON'T bump it on autosave — that would unmount
  // the editor mid-typing and lose the user's keystrokes.
  const [editorRemountKey, setEditorRemountKey] = useState(0);

  // Scratchpad — admin-only, saved on its own channel with its own version.
  // Nothing here is passed to Editor, DiffView or the PDF.
  const [scratch, setScratch] = useState<ScratchDoc>(EMPTY_SCRATCH);
  const scratchRef = useRef(scratch);
  scratchRef.current = scratch;
  const [scratchOpen, setScratchOpen] = useState(false);
  const [scratchWidth, setScratchWidth] = useState(readStoredScratchWidth);
  const [mobilePane, setMobilePane] = useState<'script' | 'scratch'>('script');
  // Which sidebar panel the icon rail has open as a drawer, if any.
  const [railPanelKey, setRailPanelKey] = useState<string | null>(null);
  const mainGridRef = useRef<HTMLDivElement>(null);
  const scratchResizeCleanupRef = useRef<(() => void) | null>(null);
  // Chooses drawer versus stacked presentation for the sidebar panels. The
  // panels themselves stay at one tree position in both modes (SidebarPanels),
  // so flipping this never remounts them.
  const [isDesktop, setIsDesktop] = useState(() => !isMobileViewport());
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => () => scratchResizeCleanupRef.current?.(), []);

  // Selection + comment state
  const [selection, setSelection] = useState<SelectionInfo | null>(null);
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [draftAnchor, setDraftAnchor] = useState<{ from: number; to: number; text: string } | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Polled presence — admin sees a "Client viewing now" indicator when a
  // client tab has heartbeated within the last 60s.
  const [lastHeartbeatAt, setLastHeartbeatAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function tick() {
      if (cancelled) return;
      try {
        const r = await adminApi.getPresence(id!);
        if (!cancelled) setLastHeartbeatAt(r.last_heartbeat_at);
      } catch {
        // ignore — next tick retries
      }
    }
    void tick();
    const pollId = window.setInterval(tick, 20_000);
    // Tick `now` every 5s so the badge can fade out within ~5s of staleness.
    const nowId = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      window.clearInterval(nowId);
    };
  }, [id]);
  const isClientPresent = lastHeartbeatAt !== null && now - lastHeartbeatAt < 60_000;

  useEffect(() => {
    if (!id) return;
    adminApi
      .getScript(id)
      .then((s) => {
        setScript(s);
        setVersion(s.doc_version);
        setStatus(s.status);
        setFeatures(s.features);
        setScratch(parseScratch(s.scratch_json));
        setScratchOpen(s.scratch_open);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Failed to load.'));
  }, [id]);

  useEffect(() => {
    if (!script || !new URLSearchParams(location.search).has('osView')) return;
    if (consumedEntry.current === location.key) return;
    consumedEntry.current = location.key;
    const entry = resolveOsView(script, location.search);
    setViewNotice(entry.unavailable);
    navigate(entry.url + location.hash, { replace: true });
  }, [script, location, navigate]);

  async function handleStatusChange(next: ScriptStatus) {
    if (!id) return;
    // 2A: a later round opens the Publish dialog; approving with unpublished
    // changes asks first.
    if (await publish.interceptStatusChange(next)) return;
    const result = await adminApi.setStatus(id, next);
    setStatus(result.status);
  }

  async function handleFeaturesChange(next: ScriptFeatures) {
    if (!id) return;
    const result = await adminApi.setFeatures(id, next);
    setFeatures(result.features);
  }

  async function handleRotateSlug() {
    if (!id) return;
    const result = await adminApi.rotateSlug(id);
    setScript((prev) => (prev ? { ...prev, slug: result.slug } : prev));
  }

  async function reloadScriptAfterAcceptedSuggestion() {
    if (!id) return;
    const fresh = await adminApi.getScript(id);
    setScript(fresh);
    setVersion(fresh.doc_version);
    setCompareMode(false);
    // 2A: a restore or an accept changes the working copy, not what the client reads.
    void publish.refresh().catch(() => {});
    // Doc was overwritten by either a suggestion accept or a version restore —
    // remount the editor to load the new content.
    setEditorRemountKey((k) => k + 1);
  }

  const adminSuggestion = useAdminSuggestion(
    id,
    features.suggestions,
    reloadScriptAfterAcceptedSuggestion,
  );

  const autosave = useAutosave({
    scriptId: id ?? '',
    initialVersion: script?.doc_version ?? 1,
    onVersionChanged: setVersion,
    onSaved: (result) => publish.onSaved(result),
  });

  const publish = usePublishControls({
    script,
    status,
    setStatus,
    version,
    flush: () => autosave.flush(),
    onOpenAdminSettings: () => setRailPanelKey('admin-settings'),
    onReviewSuggestion: () => setRailPanelKey('suggestions'),
  });

  const scratchAutosave = useScratchAutosave({
    scriptId: id ?? '',
    initialVersion: script?.scratch_version ?? 0,
  });

  function updateScratch(next: ScratchDoc) {
    setScratch(next);
    // Block editors emit an update as they normalise on mount. If nothing
    // actually changed, do not burn a scratch_version: a no-op save would
    // push the same script's pad in another tab to stale for no reason.
    const unchanged = serializeScratch(next) === serializeScratch(scratchRef.current);
    scratchRef.current = next;
    if (unchanged) return;
    scratchAutosave.schedule(next);
  }

  function toggleScratchpad() {
    const next = !scratchOpen;
    setScratchOpen(next);
    // Opening from a phone should land you on the pad you just asked for.
    if (next) setMobilePane('scratch');
    else setRailPanelKey(null);
    if (!id) return;
    void adminApi.setScratchOpen(id, next).catch((err) => {
      console.error('scratch-open save failed', err);
      showToast('error', "Couldn't remember the scratchpad state for this script.");
    });
  }

  // ⌘⇧S / Ctrl+Shift+S from anywhere on the page. Compare mode owns the whole
  // width, so the shortcut sits out until comparing stops.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.repeat) return;
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (e.code !== 'KeyS' && e.key.toLowerCase() !== 's') return;
      if (compareMode) return;
      e.preventDefault();
      toggleScratchpad();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, scratchOpen, id]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SCRATCH_WIDTH_KEY, String(scratchWidth));
    } catch {
      // Storage disabled — the pane still resizes for this session.
    }
  }, [scratchWidth]);

  /** Drag the divider between the scratch pane and the script. */
  function startScratchResize(e: React.PointerEvent<HTMLDivElement>) {
    const grid = mainGridRef.current;
    if (!grid) return;
    e.preventDefault();
    const rect = grid.getBoundingClientRect();
    const styles = window.getComputedStyle(grid);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const inner = rect.width - padLeft - (parseFloat(styles.paddingRight) || 0);
    if (inner <= 0) return;
    const minPct = Math.min(
      Math.max((SCRATCH_COLUMN_MIN_PX / inner) * 100, SCRATCH_WIDTH_MIN),
      SCRATCH_WIDTH_MAX,
    );
    const divider = e.currentTarget;
    const pointerId = e.pointerId;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    let cleanedUp = false;

    function onMove(ev: PointerEvent) {
      const pct = ((ev.clientX - rect.left - padLeft) / inner) * 100;
      setScratchWidth(
        Math.round(Math.min(SCRATCH_WIDTH_MAX, Math.max(minPct, pct)) * 10) / 10,
      );
    }
    function cleanup() {
      if (cleanedUp) return;
      cleanedUp = true;
      divider.removeEventListener('pointermove', onMove);
      divider.removeEventListener('pointerup', cleanup);
      divider.removeEventListener('pointercancel', cleanup);
      divider.removeEventListener('lostpointercapture', cleanup);
      window.removeEventListener('blur', cleanup);
      if (divider.hasPointerCapture(pointerId)) divider.releasePointerCapture(pointerId);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      if (scratchResizeCleanupRef.current === cleanup) scratchResizeCleanupRef.current = null;
    }
    scratchResizeCleanupRef.current?.();
    scratchResizeCleanupRef.current = cleanup;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    divider.setPointerCapture(pointerId);
    divider.addEventListener('pointermove', onMove);
    divider.addEventListener('pointerup', cleanup);
    divider.addEventListener('pointercancel', cleanup);
    divider.addEventListener('lostpointercapture', cleanup);
    window.addEventListener('blur', cleanup);
  }

  /**
   * Send a scratch block into the script. The text is copied, never linked:
   * once it lands, the two are independent (see shared/scratch.ts).
   */
  function promoteScratchBlock(block: ScratchBlock, action: PromoteAction) {
    const text = scratchBodyToText(block.body);
    if (!text.trim()) {
      showToast('error', 'That block is empty, so there is nothing to send.');
      return;
    }

    if (action === 'copy') {
      const clipboard = navigator.clipboard;
      if (!clipboard) {
        showToast('error', "Couldn't copy that text.");
        return;
      }
      void clipboard.writeText(text).then(
        () => showToast('success', 'Copied'),
        () => showToast('error', "Couldn't copy that text."),
      );
      return;
    }

    if (!editor) {
      showToast('error', 'The script editor is still loading. Try again in a moment.');
      return;
    }

    // The editor stays mounted behind the phone's pane picker, preserving
    // the intended insertion point and the transaction/undo history.
    const atEnd = false;
    const inserted =
      action === 'row'
        ? insertScriptRowAtCursor(editor, text, { atEnd })
        : insertParagraphsAtCursor(editor, text, { atEnd });
    if (!inserted) {
      showToast('error', "Couldn't add that to the script.");
      return;
    }

    updateScratch({
      ...scratch,
      blocks: scratch.blocks.map((b) =>
        b.id === block.id ? { ...b, used_at: Date.now() } : b,
      ),
    });

    if (!isDesktop) {
      setMobilePane('script');
      setRailPanelKey(null);
      window.requestAnimationFrame(() => editor.commands.focus());
    }
    showToast('success', 'Inserted into the script. Use Undo to reverse it.');
  }

  // Warn before tab close / navigate away if the master draft has typed
  // changes that haven't been autosaved yet. Without this, a fast
  // close-tab after a keystroke can lose the most recent characters
  // (autosave debounces). The browser's native confirm dialog handles UX.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      warnBeforeUnload(e, shouldWarnBeforeUnload(
        autosave.state,
        scratchAutosave.state,
        scratchAutosave.hasPendingWork(),
        autosave.hasPendingWork(),
      ));
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [autosave.state, autosave.hasPendingWork, scratchAutosave.state, scratchAutosave.hasPendingWork]);

  const comments = useScriptComments({
    mode: 'admin',
    scriptId: id,
    enabled: Boolean(id && script),
  });

  // Build thread anchors. Resolved threads are hidden by default; they only
  // appear as a highlight when the user explicitly clicks them in the sidebar
  // (i.e. when they become the activeThreadId).
  const threadAnchors = useMemo<ThreadAnchor[]>(
    () =>
      comments.threads
        .filter((t) => t.status !== 'resolved' || t.id === activeThreadId)
        .map((t) => ({
          id: t.id,
          from: t.anchor_from,
          to: t.anchor_to,
          anchor_text: t.anchor_text,
          status: t.status,
        })),
    [comments.threads, activeThreadId],
  );

  const handleSelectionChange = useCallback((info: SelectionInfo) => {
    setSelection(info);
    if (info.empty) {
      setSelectionRect(null);
      return;
    }
    const docSel = window.getSelection();
    if (docSel && docSel.rangeCount > 0) {
      const rect = docSel.getRangeAt(0).getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setSelectionRect(rect);
      }
    }
  }, []);

  function startCommentDraft() {
    if (!selection || selection.empty) return;
    setDraftAnchor({ from: selection.from, to: selection.to, text: selection.text });
    setSelectionRect(null);
    // In rail mode the composer lives inside a closed drawer, so open it —
    // otherwise "+ Comment" would look like it did nothing.
    if ((!isDesktop || scratchOpen) && !compareMode) setRailPanelKey('comments');
  }

  // Suggested doc — memoized by JSON string so the 15s poll doesn't churn
  // the reference. (basedOn is replaced by the live master snapshot below.)
  const suggestedDoc = useMemo(
    () => parseDocJson(adminSuggestion.suggestion?.doc_json),
    [adminSuggestion.suggestion?.doc_json],
  );

  // Live master snapshot — fed to DiffView as the "from" side of the diff.
  // Diffing master-now → suggested instead of basedOn → suggested means
  // accepted hunks vanish naturally on the next render (master matches
  // suggested at that location), which fixes both:
  //   (a) duplication on poll — accepted hunks are gone from the diff
  //       regardless of any DiffView remount
  //   (b) position drift — every hunk's fromA/toA is in current master
  //       coords, no Mapping needed
  // We update on every editor change while in compare mode so user typing
  // is also reflected (typed text appears in the diff as "extra in master").
  const [masterDocSnapshot, setMasterDocSnapshot] = useState<unknown>(null);
  useEffect(() => {
    if (!editor || !compareMode) {
      setMasterDocSnapshot(null);
      return;
    }
    setMasterDocSnapshot(editor.getJSON());
    const handler = () => setMasterDocSnapshot(editor.getJSON());
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
  }, [editor, compareMode]);

  // 2A: the client edited the copy they were shown (the suggestion's base),
  // not Henry's working copy. Rebase their changes onto the live working copy
  // block by block, so the diff shows only what the client changed and
  // Henry's unpublished edits never appear as red deletions. Rows both sides
  // changed show the client's version and are counted for the banner.
  const compareMerge = useMemo(() => {
    const base = adminSuggestion.suggestion?.based_on_doc_json;
    const theirs = adminSuggestion.suggestion?.doc_json;
    if (masterDocSnapshot == null || !base || !theirs) return { json: null, conflicts: 0 };
    const merged = mergeDocs(base, JSON.stringify(masterDocSnapshot), theirs);
    return merged.ok ? { json: merged.docJson, conflicts: merged.conflicts } : { json: null, conflicts: 0 };
  }, [masterDocSnapshot, adminSuggestion.suggestion?.based_on_doc_json, adminSuggestion.suggestion?.doc_json]);
  // Keyed by the merged JSON string, so typing elsewhere in master does not
  // hand DiffView a new document (which would reset its dismissed hunks).
  const mergedDoc = useMemo(() => (compareMerge.json ? parseDocJson(compareMerge.json) : null), [compareMerge.json]);
  const compareDoc = { doc: mergedDoc ?? suggestedDoc, conflicts: compareMerge.conflicts };
  const diffSuggestedDoc = compareDoc.doc;

  // Per-hunk accept: hunk.fromA/toA are positions in the live master
  // (because we feed master as basedOn to DiffView). We dispatch a
  // replaceRange — the "Range" variant adjusts slice openness to fit the
  // gap, which avoids dropping content into the wrong cell of a custom
  // node (e.g. visual vs audio in a script row).
  const acceptHunk = useCallback(
    (hunk: Hunk): boolean => {
      if (!editor || !diffSuggestedDoc) return false;
      const masterState = editor.state;
      const docSize = masterState.doc.content.size;
      if (hunk.fromA < 0 || hunk.toA > docSize || hunk.fromA > hunk.toA) {
        window.alert(
          "Couldn't apply that change cleanly. Apply manually, or use Accept & overwrite for the whole suggestion.",
        );
        return false;
      }
      try {
        const suggestedNode = editor.schema.nodeFromJSON(diffSuggestedDoc);
        const slice = suggestedNode.slice(hunk.fromB, hunk.toB);
        const tr = masterState.tr.replaceRange(hunk.fromA, hunk.toA, slice);
        editor.view.dispatch(tr);
        // On a brief the field guard filters out any transaction that would
        // change the set of fields (a hunk straddling a field boundary), and
        // a filtered dispatch is a silent no-op. Report it as a failure
        // rather than leave the hunk looking applied.
        if (editor.state.doc.eq(masterState.doc)) {
          window.alert(
            "Couldn't apply that change cleanly. Apply manually, or use Accept & overwrite for the whole suggestion.",
          );
          return false;
        }
        return true;
      } catch (err) {
        console.error('per-hunk accept failed', err);
        window.alert(
          "Couldn't apply that change cleanly. Please copy it manually, or accept the whole suggestion.",
        );
        return false;
      }
    },
    [editor, diffSuggestedDoc],
  );

  function activateThread(threadId: string | null) {
    setActiveThreadId(threadId);
    if (!threadId || !editor) return;
    const t = comments.threads.find((x) => x.id === threadId);
    if (!t) return;
    const docSize = editor.state.doc.content.size;
    const from = Math.max(0, Math.min(t.anchor_from, docSize));
    const to = Math.max(from, Math.min(t.anchor_to, docSize));
    editor.commands.setTextSelection({ from, to });
    if (!isDesktop) { setMobilePane('script'); setRailPanelKey(null); }
    window.requestAnimationFrame(() => editor.commands.scrollIntoView());
  }

  async function handleMakeCutdown() {
    // The cut is built from the stored doc, so it must match the page.
    if (!(await autosave.flush())) {
      showToast('error', 'Save pending changes before making a cutdown.');
      return;
    }
    setCutdownOpen(true);
  }

  if (loadError) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-accent">{loadError}</p>
      </main>
    );
  }

  if (!script) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-faint font-mono text-[12px] uppercase tracking-[0.12em]">Loading…</p>
      </main>
    );
  }

  const initialDoc = parseDocJson(script.doc_json);
  if (!initialDoc) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <p className="text-accent text-center max-w-md leading-[1.55]">
          This script's stored document couldn't be read, so the editor can't open it. Restore an
          earlier snapshot from version history, or check the row in D1 before editing.
        </p>
      </main>
    );
  }

  const shareUrl = `${window.location.origin}/s/${script.slug}`;
  const linkShareable = isStatusShareable(status);
  // An interview brief is an ordinary script with a run of `briefField` nodes
  // in its document; the only thing the editor page adds for one is the Fields
  // drawer and the hidden-fields strip.
  const isBrief = isBriefTemplate(script.template_key);

  async function copyShareUrl() {
    if (!linkShareable) return;
    await navigator.clipboard.writeText(shareUrl).catch(() => {});
  }

  /** The freshest doc: straight from the editor, so a PDF reflects
   *  unsaved-but-typed changes rather than the last autosave. */
  function pdfInput() {
    if (!script) return null;
    return {
      title: script.title,
      client_name: script.client_name,
      status: script.status,
      // Script type doesn't carry approved_at; use null. Admin export doesn't
      // typically need the date because admin already knows.
      approved_at: null,
      slug: script.slug,
      doc_json: editor ? JSON.stringify(editor.getJSON()) : script.doc_json,
    };
  }

  async function downloadPdf() {
    const input = pdfInput();
    if (input) await downloadScriptPdf(input);
  }

  async function downloadBrief(variant: 'client' | 'full') {
    const input = pdfInput();
    if (!input) return;
    try {
      await downloadBriefPdf({ ...input, variant });
    } catch {
      showToast('error', 'That PDF did not generate. Please try again in a moment.');
    }
  }

  const hasActiveSuggestion =
    adminSuggestion.suggestion?.status === 'editing' ||
    adminSuggestion.suggestion?.status === 'submitted';

  // The scratch pane and the icon rail arrive together: the rail exists to buy
  // the script back the width the pane takes. Compare mode owns the full width,
  // so it hides the pane without persisting a change to scratch_open.
  const scratchVisible = scratchOpen && !compareMode;
  /** Desktop split view: scratch pane, divider, script, icon rail. */
  const railMode = scratchVisible && isDesktop;
  const openThreadCount = comments.threads.filter(
    (t) => t.status === 'open' || t.status === 'orphaned',
  ).length;

  /**
   * The right-hand panels, built once. The stacked sidebar and the rail's
   * overlay drawer both render these exact nodes, so a panel behaves the same
   * either way — `inRail` only decides whether it earns an icon.
   */
  const sidebarPanels: (RailPanel & { inRail: boolean })[] = [
    {
      key: 'suggestions',
      label: 'Suggestions',
      inRail: hasActiveSuggestion,
      icon: (
        <RailIcon>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </RailIcon>
      ),
      panel: (
        <SuggestionPanel
          enabled={features.suggestions}
          suggestion={adminSuggestion.suggestion}
          onStartCompare={() => {
            setRailPanelKey(null);
            setMobileComparePane('suggestion');
            setCompareMode(true);
          }}
        />
      ),
    },
    {
      key: 'notes',
      label: 'Client notes',
      inRail: true,
      icon: (
        <RailIcon>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M8 13h8" />
          <path d="M8 17h6" />
        </RailIcon>
      ),
      panel: <ClientNotesPanel key={script.id} mode="admin" scriptId={script.id} />,
    },
    {
      key: 'admin-settings',
      label: 'Admin settings',
      inRail: true,
      icon: <AdminSettingsIcon />,
      panel: (
        <AdminSettingsPanel key={script.id} drawer={railMode || !isDesktop}>
          <ClientEmailPanel
            scriptId={script.id}
            currentEmail={script.client_email}
            onSaved={(next) => {
              setScript((prev) => (prev ? { ...prev, client_email: next } : prev));
            }}
          />
          <ApproveByPanel
            scriptId={script.id}
            currentApproveBy={script.approve_by}
            onSaved={(next) => {
              setScript((prev) => (prev ? { ...prev, approve_by: next } : prev));
            }}
          />
          <ReviewChecklistPanel
            scriptId={script.id}
            templateKey={script.template_key}
            setting={script.review_checklist}
            onSaved={(next) => {
              setScript((prev) => (prev ? { ...prev, review_checklist: next } : prev));
            }}
          />
          <RecordingsPanel
            scriptId={script.id}
            scriptTitle={script.title}
            clientName={script.client_name}
            scriptTemplateKey={script.template_key}
            // Pull the script's authored interview questions once and
            // hand them down; the transcript dialog uses them to label
            // per-question segments. An empty array is the natural
            // fallback when the script has no Interview questions H2.
            questions={extractInterviewQuestions(script.doc_json) ?? []}
          />
          <DashboardSlugPanel
            projectId={script.project_id}
            currentSlug={script.dashboard_slug}
            onSaved={(next) => {
              // Mirror the new slug into local state so the panel reflects
              // it immediately without refetching the whole script.
              setScript((prev) => (prev ? { ...prev, dashboard_slug: next } : prev));
            }}
          />
        </AdminSettingsPanel>
      ),
    },
    {
      key: 'comments',
      label:
        openThreadCount > 0
          ? `Comments · ${openThreadCount} open`
          : 'Comments',
      inRail: true,
      badge: openThreadCount,
      icon: (
        <RailIcon>
          <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
        </RailIcon>
      ),
      panel: (
        <CommentSidebar
          mode="admin"
          kind={isBrief ? 'brief' : 'script'}
          threads={comments.threads}
          loading={comments.loading}
          draftAnchor={draftAnchor}
          onCancelDraft={() => setDraftAnchor(null)}
          onCreateThread={async (params) => {
            await comments.createThread({
              thread_id: params.thread_id,
              anchor_text: params.anchor_text,
              anchor_from: params.anchor_from,
              anchor_to: params.anchor_to,
              author_name: params.author_name,
              body: params.body,
            });
          }}
          onReply={async (threadId, body, author_name) => {
            await comments.reply(threadId, { thread_id: threadId, body, author_name });
          }}
          onResolve={comments.resolve}
          onReopen={comments.reopen}
          onActivateThread={activateThread}
          activeThreadId={activeThreadId}
        />
      ),
    },
  ];

  const railPanels = sidebarPanels.filter((p) => p.inRail);

  async function resetClientView() {
    const reset = await adminSuggestion.reset();
    setResetConfirming(false);
    setCompareMode(false);
    showToast(
      'success',
      reset ? 'Client view reset.' : 'Client view was already reset.',
    );
  }


  return (
    <main data-context="admin" className="mobile-editor min-h-screen bg-page text-fg">
      <Toaster />
      {viewNotice && <div role="status" className="px-5 py-3 text-accent bg-accent/10">{viewNotice}</div>}
      <EditorTopBar
        title={script.title}
        mobilePane={mobilePane}
        mobilePanel={railPanelKey}
        onMobilePane={(pane) => {
          setRailPanelKey(null);
          setMobilePane(pane);
          window.scrollTo({ top: 0 });
          if (pane === 'scratch' && !scratchOpen) toggleScratchpad();
        }}
        onMobilePanel={(key) => { setCompareMode(false); setRailPanelKey(key); window.scrollTo({ top: 0 }); }}
        onOpenClientView={() => { void (async () => {
          if (!(await autosave.flush()) || (scratchAutosave.hasPendingWork() && !(await scratchAutosave.flush()))) {
            showToast('error', 'Save pending changes before opening client review.'); return;
          }
          navigate(`/s/${script.slug}`);
        })(); }}
        kind={isBrief ? 'brief' : 'script'}
        saveState={autosave.state}
        version={version}
        saveErrorMsg={autosave.errorMsg}
        onRetrySave={() => void autosave.flush()}
        clientPresent={isClientPresent}
        status={status}
        onStatusChange={handleStatusChange}
        scratchOpen={scratchOpen}
        scratchCount={scratch.blocks.length}
        onToggleScratchpad={toggleScratchpad}
        fieldsOpen={fieldsOpen}
        onToggleFields={() => setFieldsOpen((o) => !o)}
        fieldsButtonRef={fieldsButtonRef}
        features={features}
        onFeaturesChange={handleFeaturesChange}
        onRotateSlug={handleRotateSlug}
        onOpenVersionHistory={() => setHistoryOpen(true)}
        onMakeCutdown={isBrief ? undefined : handleMakeCutdown}
        cutdownReason={cutdownReason}
        shareUrl={shareUrl}
        linkShareable={linkShareable}
        onCopyShareUrl={copyShareUrl}
        onDownloadPdf={() => void downloadPdf()}
        onDownloadBriefPdf={(v) => void downloadBrief(v)}
        onOpenShootView={
          hasShootView(script.doc_json, script.template_key)
            ? () =>
                void openShootView(`/admin/scripts/${script.id}/interview`, navigate, {
                  // A phone opens the shoot view in place, which unmounts
                  // this editor, so both autosaves land first. If either
                  // cannot, stay here with the save error visible rather
                  // than leave the edit behind.
                  beforeNavigate: async () => {
                    try {
                      const saved = await autosave.flush();
                      // A stale scratch pane (a 409 from another tab) makes
                      // flush() report false forever with nothing queued;
                      // only block on scratch work that is actually pending.
                      const scratchSaved = scratchAutosave.hasPendingWork()
                        ? await scratchAutosave.flush()
                        : true;
                      if (saved && scratchSaved) return true;
                    } catch {
                      // fall through to the toast
                    }
                    showToast('error', 'Your last change did not save. Check the editor before you leave.');
                    return false;
                  },
                })
            : null
        }
        hasActiveSuggestion={hasActiveSuggestion}
        onResetClientView={() => setResetConfirming(true)}
        compareMode={compareMode}
        onStopComparing={() => setCompareMode(false)}
        {...publish.topBarProps}
      />
      {publish.modals}

      {autosave.state === 'stale' && (
        <div className="bg-accent/10 border-b border-accent/40 text-accent text-[13px] px-6 py-3 text-center">
          A newer version of this script exists (probably from another tab).{' '}
          <button onClick={() => window.location.reload()} className="underline font-medium">
            Reload to continue.
          </button>
        </div>
      )}

      {compareMode && adminSuggestion.suggestion && (
        <CompareReviewBar
          suggestion={adminSuggestion.suggestion}
          onAccept={async () => {
            try {
              await adminSuggestion.accept();
            } catch (err) {
              // Re-read the suggestion so the bar reflects reality if the
              // failure was someone else acting on it, then let the bar
              // surface the message. The diff itself is already re-based on
              // every keystroke (masterDocSnapshot), so there's nothing stale
              // to reload for the per-hunk recovery path.
              await adminSuggestion.refresh();
              throw err;
            }
          }}
          onMarkReviewed={async () => {
            await adminSuggestion.markReviewed();
            setCompareMode(false);
          }}
          onReject={async (note) => {
            await adminSuggestion.reject(note);
            setCompareMode(false);
          }}
        />
      )}

      {compareMode && !isDesktop && <nav className="mobile-compare-tabs" aria-label="Compare panes">
        <button aria-pressed={mobileComparePane === 'suggestion'} onClick={() => setMobileComparePane('suggestion')}>Client suggestion</button>
        <button aria-pressed={mobileComparePane === 'master'} onClick={() => setMobileComparePane('master')}>Master draft</button>
      </nav>}
      <div
        ref={mainGridRef}
        style={
          scratchVisible
            ? ({ '--scratch-w': `${scratchWidth}%` } as React.CSSProperties)
            : undefined
        }
        className={`mobile-editor-grid max-w-7xl mx-auto px-6 md:px-8 py-8 md:py-12 ${
          scratchVisible
            ? 'hsp-scratch-grid space-y-6 md:space-y-0'
            : `grid gap-6 md:gap-8 ${
                compareMode
                  ? 'grid-cols-1 md:grid-cols-2'
                  : 'grid-cols-1 md:grid-cols-[1fr_340px]'
              }`
        }`}
      >
        {scratchVisible && (
          <div
            data-print-hide
            className={`flex-col md:sticky md:top-[68px] ${
              mobilePane === 'scratch' && !railPanelKey ? 'flex' : 'hidden md:flex'
            }`}
          >
            <ScratchpadPane
              doc={scratch}
              onChange={updateScratch}
              onPromote={promoteScratchBlock}
              onCollapse={toggleScratchpad}
              saveState={scratchAutosave.state}
              errorMsg={scratchAutosave.errorMsg}
              showCollapse={isDesktop}
            />
          </div>
        )}

        {scratchVisible && (
          <div
            data-print-hide
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the scratchpad"
            aria-valuenow={Math.round(scratchWidth)}
            aria-valuemin={SCRATCH_WIDTH_MIN}
            aria-valuemax={SCRATCH_WIDTH_MAX}
            tabIndex={0}
            onPointerDown={startScratchResize}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft')
                setScratchWidth((w) => Math.max(SCRATCH_WIDTH_MIN, w - 2));
              else if (e.key === 'ArrowRight')
                setScratchWidth((w) => Math.min(SCRATCH_WIDTH_MAX, w + 2));
              else return;
              e.preventDefault();
            }}
            className="hsp-scratch-divider hidden md:flex self-stretch"
          />
        )}

        <div className={`editor-script-column min-w-0 ${
          !isDesktop && (railPanelKey || (scratchVisible && mobilePane !== 'script') || (compareMode && mobileComparePane !== 'master')) ? 'hidden' : ''
        }`}>
          <Toolbar editor={editor} commentsOpen={!compareMode && (!railMode || railPanelKey === 'comments')} />
          <div data-print-hide className="flex items-center justify-between gap-3 mb-2">
            {compareMode && (
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                // Master draft · editable
              </span>
            )}
            <div className="flex items-center gap-3">
              {!isBrief && <><RuntimeBadge editor={editor} /><ReadThrough editor={editor} /></>}
              <WordCount editor={editor} />
            </div>
          </div>
          <div className="mobile-editor-paper mt-1 bg-card rounded-md border border-line px-6 md:px-10 py-10 md:py-14 min-h-[60vh]">
            <Editor
              key={`master-${script.id}-${editorRemountKey}`}
              initialDoc={initialDoc}
              onUpdate={(doc) => autosave.schedule(doc)}
              onReady={setEditor}
              onSelectionChange={handleSelectionChange}
              threads={threadAnchors}
              activeThreadId={activeThreadId}
            />
            {isBrief && <HiddenFieldsStrip editor={editor} />}
          </div>
          <p data-print-hide className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint mt-6 text-center select-all">
            {shareUrl}
          </p>
        </div>

        {compareMode && adminSuggestion.suggestion && (
          <div data-print-hide className={`mobile-diff min-w-0 ${!isDesktop && mobileComparePane !== 'suggestion' ? 'hidden' : ''}`}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent">
                // Client suggestion · read-only
              </span>
            </div>
            {compareDoc.conflicts > 0 && (
              <p role="status" className="mb-2 rounded-md border border-gold/50 bg-gold/10 px-4 py-2.5 text-[12.5px] text-fg leading-[1.5]">
                You've changed {compareDoc.conflicts === 1 ? 'a row' : `${compareDoc.conflicts} rows`} the client also changed.
                Their version is shown there: tick the changes you want, then mark it reviewed.
              </p>
            )}
            <div className="mt-1 bg-card rounded-md border border-accent/60 px-6 md:px-10 py-10 md:py-14 min-h-[60vh]">
              {masterDocSnapshot != null && diffSuggestedDoc != null && (
                <DiffView
                  key={`diff-${adminSuggestion.suggestion.id}`}
                  basedOnDoc={masterDocSnapshot}
                  suggestedDoc={diffSuggestedDoc}
                  resetKey={adminSuggestion.suggestion.doc_json}
                  onAcceptHunk={acceptHunk}
                />
              )}
              {!suggestedDoc && (
                <p className="text-accent text-[13px] leading-[1.55]">
                  This suggestion's document couldn't be read, so there's nothing to compare.
                  Ask the client to resend it, or reset their view to start a fresh draft.
                </p>
              )}
            </div>
          </div>
        )}
        <div className={compareMode ? 'hidden' : 'contents'}>
          <SidebarPanels
            panels={sidebarPanels}
            mode={!isDesktop ? 'mobile' : railMode ? 'drawer' : 'stacked'}
            openKey={railPanelKey}
            onClose={() => setRailPanelKey(null)}
            stackedClassName={scratchVisible ? 'mt-6' : ''}
            stackedHidden={scratchVisible && mobilePane !== 'script'}
          />
        </div>

        {railMode && (
          <SidebarRail
            className="hidden md:flex"
            panels={railPanels}
            openKey={railPanelKey}
            onToggle={(key) => setRailPanelKey((k) => (k === key ? null : key))}
          />
        )}
      </div>

      {!compareMode && (
        <SelectionPill rect={draftAnchor || (!isDesktop && (mobilePane === 'scratch' || railPanelKey)) ? null : selectionRect} onAdd={startCommentDraft} />
      )}

      {isBrief && (
        <FieldsPanel
          editor={editor}
          open={fieldsOpen}
          onClose={() => setFieldsOpen(false)}
          returnFocusTo={fieldsButtonRef}
        />
      )}

      <VersionHistoryPanel
        scriptId={script.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onRestored={reloadScriptAfterAcceptedSuggestion}
      />

      {cutdownOpen && (
        <CutdownDialog
          scriptId={script.id}
          title={script.title}
          onClose={() => setCutdownOpen(false)}
          onCreated={(result) => {
            // Henry stays on the source; the toast offers the shortest draft.
            setCutdownOpen(false);
            const toast = createdToast(result);
            const open = toast.open;
            showToast('success', toast.text, open ? {
              action: { label: open.label, onClick: () => navigate(`/admin/scripts/${open.id}`) },
            } : undefined);
          }}
        />
      )}

      {resetConfirming && (
        <ConfirmModal
          eyebrow="Client link"
          title="Reset the client's view?"
          body="This clears the editing banners from the client link and discards the in-progress suggested-edits draft, including any test edits. The script itself isn't touched; the client will see the latest version you sent. If an accepted edit already changed the script, restore it from Version history instead."
          confirmLabel="Reset client view"
          busyLabel="Resetting…"
          onCancel={() => setResetConfirming(false)}
          onConfirm={resetClientView}
        />
      )}
    </main>
  );
}
